import { useState, useEffect, useCallback, useRef } from "react";
import { logErrorToBackend } from "../utilities/helpers";

const isLocalStorageAvailable = () => {
  try {
    const x = "__storage_test__";
    localStorage.setItem(x, x);
    localStorage.removeItem(x);
    return true;
  } catch (e) {
    return false;
  }
};

type UseLocalStorageOptions = {
  /**
   * When false, ignore persisted data on mount and use provided initial value
   * until caller writes fresh state (network-first behavior).
   */
  hydrateFromStorage?: boolean;
  /**
   * When false, avoid writing initial value back to storage until caller sets
   * a value. Useful with hydrateFromStorage=false to avoid storing placeholders.
   */
  persistInitialValue?: boolean;
};

export function useLocalStorage<T>(
  key: string,
  initialValue: T | (() => T),
  options?: UseLocalStorageOptions
) {
  const hydrateFromStorage = options?.hydrateFromStorage ?? true;
  const persistInitialValue = options?.persistInitialValue ?? true;
  const hasExternalSetRef = useRef(false);

  const [value, setValue] = useState<T>(() => {
    if (hydrateFromStorage) {
      const jsonValue = localStorage.getItem(key);

      try {
        if (jsonValue != null) return JSON.parse(jsonValue);
      } catch (error) {}

      // Handle lazy initialization
      if (typeof initialValue === "function") {
        return (initialValue as () => T)();
      } else {
        return initialValue;
      }
    }

    // For IndexedDB-based storage, start with initialValue
    if (typeof initialValue === "function") {
      return (initialValue as () => T)();
    } else {
      return initialValue;
    }
  });

  const saveValue = useCallback(async () => {
    if (!persistInitialValue && !hasExternalSetRef.current) {
      return;
    }

    if (isLocalStorageAvailable()) {
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch (error) {
        logErrorToBackend({
          name: "LocalStorage",
          message: error,
          stack: `Unable to write to localStorage for key ${key}`,
          componentStack: "useLocalStorage",
          href: window.location.href,
        });
      }
    } else {
      logErrorToBackend({
        name: "LocalStorage",
        message: "LocalStorage is not available",
        stack: `Unable to write to localStorage for key ${key}`,
        componentStack: "useLocalStorage",
        href: window.location.href,
      });
    }
  }, [key, persistInitialValue, value]);

  useEffect(() => {
    saveValue();
  }, [key, value, saveValue]);

  const setTrackedValue = useCallback(
    (nextValue: React.SetStateAction<T>) => {
      hasExternalSetRef.current = true;
      setValue(nextValue);
    },
    [setValue]
  );

  return [value, setTrackedValue] as [typeof value, typeof setTrackedValue];
}
